/**
 * The boot mark's frames, as a pure function of the loop's phase: where each
 * of the app icon's three blocks stands, and how far the ink has risen.
 *
 * `boot-mark-svg.ts` turns these frames into the SVG animation the boot
 * screen draws. This file is pure, so `boot-mark-frames.test.ts` can hold the
 * loop, and the Vite config can import it to write the same SVG into
 * index.html.
 *
 * **The phone draws the same loop.** `packages/ui/src/components/boot-mark-frames.ts`
 * carries these frames as UI-thread worklets for the phone's `BootMark`, and
 * its test holds the two equal frame by frame: a change here fails there until
 * it is carried over.
 *
 * **The blocks are `LogoMark`'s** (`~/components/kit/logo.tsx`), in its own
 * viewBox units: the tall bar, the top block and the foot. A test reads that
 * file and holds the two equal, so the mark at rest is the app's logo.
 *
 * **One loop, from rest.** Phase 0 is the whole mark in full ink — the still,
 * and a real frame of the loop (*Motion*). It holds, the ink sinks from the top
 * while the blocks shrink towards the point where the two gaps cross, the mark
 * is gone for a moment, then it plays back the other way: the blocks grow out
 * of that point one after another, bar first, and the ink rises from the
 * bottom until the mark is whole again.
 */
import { easing } from '../../graphics/motion';

/** The full visible round trip: hold, sink, gone, rise, hold. */
export const PERIOD_MS = 1600;

/** `LogoMark`'s viewBox: `109 102 806 820`. */
export const MARK_BOX = { x: 109, y: 102, width: 806, height: 820 } as const;

/** Left, top, right, bottom. */
export type Box = readonly [number, number, number, number];
/** Upper-left, upper-right, lower-right, lower-left. */
export type Corners = readonly [number, number, number, number];

export type MarkBlock = { readonly box: Box; readonly corners: Corners };

const R = 108;

/** The three blocks at rest: `LogoMark`'s rect and two paths. */
export const MARK_BLOCKS: readonly MarkBlock[] = [
  { box: [109, 102, 484, 921], corners: [R, R, R, R] },
  { box: [539, 102, 914, 566], corners: [R, R, 0, R] },
  { box: [539, 621, 914, 921], corners: [0, R, R, R] },
];

/** Where the gap beside the bar crosses the gap between the two blocks: the blocks gather here. */
const JUNCTION = [(484 + 539) / 2, (566 + 621) / 2] as const;

/**
 * Each block as it first shows: its size as a share of its own, and how much
 * later than the bar it starts, as a share of the rise. The bar comes first,
 * then the foot, then the top block, as in the motion study the icon was
 * animated from. A block keeps its proportions as it grows, so every frame is
 * the block at rest moved and scaled (`blockTransform`), which is what lets a
 * CSS animation draw it.
 */
const ARRIVALS = [
  { from: 0.45, delay: 0 },
  { from: 0.3, delay: 0.2 },
  { from: 0.4, delay: 0.1 },
] as const;

/** How far towards the junction a block's centre starts, as a share of the way. */
const GATHER = 0.5;
/** How much of the rise one block's own growth takes. */
const GROWTH_SPAN = 0.75;
/** The ink's soft edge, as a share of the blocks' height. */
const INK_EDGE = 0.6;

/** The loop's windows, as shares of the period. The rest of the period holds the whole mark. */
export const SINK = { from: 0.24, to: 0.56 } as const;
export const RISE = { from: 0.6, to: 0.92 } as const;

export type BootMarkFrame = {
  readonly blocks: readonly MarkBlock[];
  /** 0: no ink at all. 1: the whole mark in full ink. */
  readonly ink: number;
};

const lerp = (from: number, to: number, amount: number): number => from + (to - from) * amount;
const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

/** The loop's phase, 0 to 1, `time` milliseconds after it started at rest. */
export const bootMarkPhase = (time: number): number => {
  if (!Number.isFinite(time) || time <= 0) return 0;
  return (time % PERIOD_MS) / PERIOD_MS;
};

/** One block `presence` of the way from its first showing (0) to its rest (1). */
const blockAt = (rest: MarkBlock, from: number, presence: number): MarkBlock => {
  if (presence >= 1) return rest;
  const [left, top, right, bottom] = rest.box;
  const scale = lerp(from, 1, presence);
  const halfWidth = ((right - left) * scale) / 2;
  const halfHeight = ((bottom - top) * scale) / 2;
  const pull = GATHER * (1 - presence);
  const x = lerp((left + right) / 2, JUNCTION[0], pull);
  const y = lerp((top + bottom) / 2, JUNCTION[1], pull);
  const [upperLeft, upperRight, lowerRight, lowerLeft] = rest.corners;
  return {
    box: [x - halfWidth, y - halfHeight, x + halfWidth, y + halfHeight],
    corners: [upperLeft * scale, upperRight * scale, lowerRight * scale, lowerLeft * scale],
  };
};

/**
 * `block` as the block at `rest` scaled by `scale` about the viewBox's own
 * corner and then moved by `x`, `y`: a CSS `transform` with its origin at
 * `0 0`, which is how the boot mark's SVG animates it.
 */
export const blockTransform = (rest: MarkBlock, block: MarkBlock): { readonly scale: number; readonly x: number; readonly y: number } => {
  const scale = (block.box[2] - block.box[0]) / (rest.box[2] - rest.box[0]);
  return { scale, x: block.box[0] - scale * rest.box[0], y: block.box[1] - scale * rest.box[1] };
};

/** The rise, `progress` of the way through: 0 draws nothing, 1 is the mark at rest. */
const riseAt = (progress: number): BootMarkFrame => {
  const grow = easing('out');
  const blocks = MARK_BLOCKS.map((rest, index) => {
    const arrival = ARRIVALS[index] ?? ARRIVALS[0];
    return blockAt(rest, arrival.from, grow(clamp01((progress - arrival.delay) / GROWTH_SPAN)));
  });
  return { blocks, ink: grow(clamp01(progress)) };
};

/**
 * The frame at `phase` (0 to 1). The sink is the rise played backwards, so
 * the mark leaves the way it came.
 */
export const bootMarkFrame = (phase: number): BootMarkFrame => {
  const u = clamp01(phase);
  if (u < SINK.from || u >= RISE.to) return riseAt(1);
  if (u < SINK.to) return riseAt(1 - (u - SINK.from) / (SINK.to - SINK.from));
  if (u < RISE.from) return riseAt(0);
  return riseAt((u - RISE.from) / (RISE.to - RISE.from));
};

/**
 * The ink's edge on `frame`, in viewBox units: no ink above `clear`, full ink
 * below `solid`, a straight fade between. It is measured on the blocks as
 * they stand, not on the mark at rest, so small blocks gathered at the
 * junction already show the ink coming up through them. It rises from their
 * foot until it has passed over their top.
 */
export const inkEdge = ({ blocks, ink }: BootMarkFrame): { readonly clear: number; readonly solid: number } => {
  const top = Math.min(...blocks.map(({ box }) => box[1]));
  const height = Math.max(...blocks.map(({ box }) => box[3])) - top;
  const clear = top + height * (1 - clamp01(ink) * (1 + INK_EDGE));
  return { clear, solid: clear + height * INK_EDGE };
};
